import { Icon, Text, Button, TextButton } from "@amzn/storm-ui";
import { close } from "@amzn/storm-ui-icons";
import { useAsync, useIntervalEffect } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { Paths } from "../../constants/paths";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { windowStore } from "../../store/window";
import { workbenchStore } from "../../store/workbench";
import { TabQueryParamValue } from "../../types/navigation";
import { isLoading } from "../../utils/api.utils";
import { getAdvertiserId, IHubQueryParam } from "../../utils/permission.utils";
import { NO_OPPORTUNITY_DROPDOWN_ITEM } from "../../utils/salesforce.utils";
import { logWithTakt } from "../../utils/telemetry.utils";
import { getSearchParams } from "../../utils/url.utils";

const Topbar = styled.div`
    position: sticky;
    top: 0;
    height: 45px;
    padding: 10px 25px;
    background-color: #fbfbfc;
    border-bottom: 2px solid #d9dee4;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
`;

const TopbarGrouping = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

export const ModalFooterCancelButton = styled(TextButton)`
    margin-right: ${({ theme }) => theme.spacing.small.default};
`;

export const ActionBar: React.FunctionComponent = () => {
    const navigate = useNavigate();

    const executionId = React.useRef("");
    const advertiserId = getAdvertiserId();

    const intervalRefreshRateMS = 2000; // 2 seconds;

    // Needed because it may take a few calls before success
    const [isCreating, setIsCreating] = React.useState<boolean>(false);

    const [pathname] = useStore(windowStore, (state) => [state.pathname]);
    const [resetWorkbench, title, opportunityId, pptxAssets, getTotalSlides] =
        useStore(workbenchStore, (state) => [
            state.resetWorkbench,
            state.title,
            state.opportunityId,
            state.pptxAssets,
            state.getTotalSlides,
        ]);

    const disableGenerateInsight = useMemo(() => {
        return title.trim().length === 0 || getTotalSlides() === 0;
    }, [title]);

    const [generationState, generationAction] = useAsync(async () => {
        if (!isEmpty(executionId.current)) {
            await (
                await getCdbApiClient()
            ).getGeneratedInsight({
                advertiserId,
                executionId: executionId.current,
            });
            setIsCreating(false);
            resetWorkbench();
            const urlParams = getSearchParams(
                {
                    [IHubQueryParam.INITIAL_TAB]:
                        TabQueryParamValue.GENERATED_INSIGHTS,
                },
                [IHubQueryParam.INITIAL_TAB],
            );
            const redirectPath = `/${urlParams}`;
            navigate(redirectPath);
            navigate(0);
        }
    });

    useIntervalEffect(() => {
        void generationAction.execute();
    }, intervalRefreshRateMS);

    const [generateInsightState, generateInsightAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).createCuratedInsight({
            advertiserId: getAdvertiserId(),
            pptxAssets: Array.from(pptxAssets.values()),
            title,
            opportunityId:
                opportunityId === NO_OPPORTUNITY_DROPDOWN_ITEM.value
                    ? ""
                    : opportunityId,
            origin: `${origin}${pathname}${getSearchParams({ [IHubQueryParam.INITIAL_TAB]: TabQueryParamValue.GENERATED_INSIGHTS }, [IHubQueryParam.INITIAL_TAB])}`,
        });
        executionId.current = data.executionId!;
        setIsCreating(true);
        void generationAction.execute();
        logWithTakt(TELEMETRY_ID.CURATE_INSIGHT_REQUEST, {
            executionId: data.executionId!,
            opportunityId,
        });

        return data;
    });

    return (
        <Topbar data-testid="workbench-action-bar">
            <TopbarGrouping>
                <Icon
                    data-testid="workbench-action-exit"
                    type={close}
                    onClick={() =>
                        navigate(`${Paths.DISCOVERY}${getSearchParams()}`)
                    }
                    style={{ cursor: "pointer" }}
                />
                <Text renderAs="span" size="large">
                    Insight workbench
                </Text>
            </TopbarGrouping>
            <TopbarGrouping>
                <ModalFooterCancelButton
                    onClick={() => {
                        resetWorkbench();
                        navigate(`${Paths.DISCOVERY}${getSearchParams()}`);
                    }}
                    data-testid="workbench-action-reset"
                >
                    Clear Workbench
                </ModalFooterCancelButton>
                <Button
                    data-testid="workbench-action-generate-insight"
                    type="submit"
                    variant="primary"
                    loading={
                        isLoading(generateInsightState.status) ||
                        isLoading(generationState.status) ||
                        isCreating
                    }
                    onClick={() => void generateInsightAction.execute()}
                    disabled={disableGenerateInsight}
                >
                    Generate Insight
                </Button>
            </TopbarGrouping>
        </Topbar>
    );
};
